import { memo, useCallback, useMemo, useState } from 'react';
import {
  LayoutChangeEvent,
  Platform,
  StyleSheet,
  Text,
  View,
  ViewProps,
} from 'react-native';
import { commonStyles } from '../common/styles';
import Utils from '../utils/utils';
import { FocusStateType, useFocusState } from '../common/hooks';
import AppColors from '../common/colors';
import KeyWrapper from './key_wrapper';
import TvPressable from './tv_pressable';
import { VoidCb } from '../types/others';
import useGlobalStyles from '../common/style_context';
import AppIcon, { AppIcons } from './app_icon';
import { observer } from 'mobx-react-lite';
import { computed } from 'mobx';

type Props = {
  enabled?: boolean;
  submitText: string;
  onType?: (n: number) => void;
  onRemove?: () => void;
  onSubmit?: () => void;
  onPrev?: () => void;
  onNext?: () => void;
};

const DigitalKeyboard = memo<Props>(
  ({ enabled, submitText, onType, onRemove, onSubmit, onPrev, onNext }) => {
    const [itemWidth, setItemWidth] = useState<number>();
    const focusState = useFocusState(14, enabled, 13);

    const select = useCallback(
      (index: number) => {
        focusState.focused = index;
        if (index < 9) {
          onType?.(index + 1);
          return;
        }

        switch (index) {
          case 9:
            onPrev?.();
            break;
          case 10:
            onType?.(0);
            break;
          case 11:
            onNext?.();
            break;
          case 12:
            onRemove?.();
            break;
          case 13:
            onSubmit?.();
            break;
        }
      },
      [onType, onRemove, onSubmit, onPrev, onNext],
    );
    const up = useCallback((keyUp?: boolean) => {
      if (keyUp || focusState.focused === undefined) return;
      if (focusState.focused > 2) focusState.decrement(3);
    }, []);
    const down = useCallback((keyUp?: boolean) => {
      if (keyUp || focusState.focused === undefined) return;
      if (focusState.focused < 12) focusState.increment(3);
    }, []);
    const left = useCallback((keyUp?: boolean) => {
      if (keyUp || focusState.focused === undefined) return;
      const p = focusState.focused;
      if (Math.floor(p / 3) === Math.floor((p - 1) / 3)) focusState.decrement();
    }, []);
    const right = useCallback((keyUp?: boolean) => {
      if (keyUp || focusState.focused === undefined) return;
      const p = focusState.focused;
      if (Math.floor(p / 3) === Math.floor((p + 1) / 3)) focusState.increment();
      else if (p + 1 >= 13) focusState.focused = 13;
    }, []);
    const confirm = useCallback(() => {
      if (focusState.focused === undefined) return;
      select(focusState.focused);
    }, [select]);

    const [prevPress, nextPress, backspacePress, submitPress] = useMemo(() => {
      return [
        select.bind(null, 9),
        select.bind(null, 11),
        select.bind(null, 12),
        select.bind(null, 13),
      ];
    }, [select]);

    const onLayout = useCallback(
      (e: LayoutChangeEvent) => setItemWidth(e.nativeEvent.layout.width),
      [],
    );

    const rows = useMemo(
      () =>
        digits.map((e, i) => (
          <KeyRow key={i} digits={e} focusState={focusState} onPress={select} />
        )),
      [select],
    );

    return (
      <KeyWrapper
        enabled={enabled}
        up={up}
        down={down}
        left={left}
        right={right}
        confirm={confirm}
        backspace={onRemove}
        digit={onType}
        style={commonStyles.f1}>
        {rows}
        <View style={commonStyles.f1r}>
          <IconKeyItem
            icon={AppIcons.chevron_up}
            index={9}
            focusState={focusState}
            onLayout={onLayout}
            onPress={prevPress}
          />
          <KeyItem
            value={0}
            overridedFocus={10}
            focusState={focusState}
            onPress={select}
          />
          <IconKeyItem
            icon={AppIcons.chevron_down}
            index={11}
            focusState={focusState}
            onPress={nextPress}
          />
        </View>
        {itemWidth && (
          <View style={commonStyles.f1r}>
            <IconKeyItem
              icon={AppIcons.backspace}
              width={itemWidth}
              index={12}
              focusState={focusState}
              onPress={backspacePress}
            />
            <SubmitButton
              text={submitText}
              index={13}
              focusState={focusState}
              onPress={submitPress}
            />
          </View>
        )}
      </KeyWrapper>
    );
  },
);
DigitalKeyboard.displayName = 'TvDigitalKeyboard';

type RowProps = {
  digits: number[];
  focusState: FocusStateType;
  onPress?: (index: number) => void;
};

const KeyRow = memo<RowProps>(({ digits, focusState, onPress }) => {
  const items = useMemo(
    () =>
      digits.map(e => (
        <KeyItem key={e} value={e} focusState={focusState} onPress={onPress} />
      )),
    [digits, onPress],
  );
  return <View style={commonStyles.f1r}>{items}</View>;
});

type ItemProps = {
  value: number;
  overridedFocus?: number;
  focusState: FocusStateType;
  onPress?: (index: number) => void;
};

const bgColor = '#292929';

const itemStyles = [
  `f=1,jc,ac,bg=${bgColor},mb=20,mh=10,br=6,bw=3,bc=transparent`,
  `f=1,jc,ac,bg=${bgColor},mb=20,mh=10,br=6,bw=3,bc=${AppColors.mainBlue}`,
  'fs=36,fw=700,c=white',
];

const KeyItem = observer<ItemProps>(
  ({ value, overridedFocus, focusState, onPress }) => {
    const focused = computed(
      () => focusState.focused === (overridedFocus ?? value - 1),
    ).get();

    const [container, focusedContainer, textStyle] =
      useGlobalStyles(itemStyles);

    const press = useCallback(
      () => onPress?.(overridedFocus ?? value - 1),
      [value, overridedFocus, onPress],
    );

    const style = useMemo(
      () => (focused ? focusedContainer : container),
      [focused, container, focusedContainer],
    );

    return (
      <TvPressable onPress={press} style={style}>
        <Text style={textStyle}>{value}</Text>
      </TvPressable>
    );
  },
);

type IconItemProps = {
  icon: AppIcons;
  width?: number;
  index: number;
  focusState: FocusStateType;
  onLayout?: ViewProps['onLayout'];
  onPress?: VoidCb;
};

const IconKeyItem = observer<IconItemProps>(
  ({ icon, width, index, focusState, onLayout, onPress }) => {
    const focused = computed(() => focusState.focused === index).get();

    const [container, focusedContainer] = useGlobalStyles(itemStyles);

    const style = useMemo(() => {
      const style = focused ? focusedContainer : container;
      if (width === undefined) return style;
      //@ts-ignore flex: 'none' not specified on native
      return StyleSheet.compose(style, {
        width,
        flex: Platform.OS === 'web' ? 'none' : 0,
      });
    }, [focused, width, container, focusedContainer]);

    return (
      <TvPressable onPress={onPress} onLayout={onLayout} style={style}>
        <AppIcon name={icon} color={'white'} size={48} />
      </TvPressable>
    );
  },
);

type SubmitProps = { text: string } & Pick<
  IconItemProps,
  'focusState' | 'index' | 'onPress'
>;

const SubmitButton = observer<SubmitProps>(
  ({ text, index, focusState, onPress }) => {
    const focused = computed(() => focusState.focused === index).get();

    const [container, focusedContainer, textStyle] =
      useGlobalStyles(itemStyles);

    const style = useMemo(
      () => (focused ? focusedContainer : container),
      [focused, container, focusedContainer],
    );

    return (
      <TvPressable onPress={onPress} style={style}>
        <Text style={textStyle}>{text}</Text>
      </TvPressable>
    );
  },
);

const digits = Utils.chunk(
  Array.from({ length: 9 }, (_, i) => i + 1),
  3,
);

export default DigitalKeyboard;
